import React, { Component } from 'react';
import _ from 'lodash';
import { set } from 'lodash/fp';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { Question, QuestionGroup } from './Question';
import { InputRowWrapper, Button } from './styles';
import {
  Flex, Text, ActionNote, DisabledActionButton,
} from './components';
import Axios from './request';
import ADP_HomeOrgUnit from './ADP_HomeOrgUnit';
import { isActionAssigneeMatchCompanyStaffV2 } from '../../utils';
import { getErrorMessage } from '../../assets/js/utils';
import { initializeSectionTitleFlag, setSectionTitleFlag, renderSectionTitleBasedOnFlag} from './ADPFormCategoryTitleHelper';
import { PageTitle } from './biz-components/page-title';

const questionOptionsUseNameKeys = [
  '/applicantOnboarding/applicantPersonalProfile/socialInsurancePrograms/nameCode/code',
  '/applicantOnboarding/applicantWorkerProfile/job/occupationalClassifications/classificationID/code',
  '/applicantOnboarding/applicantTaxProfile/usFederalTaxInstruction/federalIncomeTaxInstruction/taxWithholdingStatus/statusCode/code',
];

const usStatesFieldKeys = [
  '/applicantOnboarding/applicantPersonalProfile/legalAddress/countryCode',
  '/applicantOnboarding/applicantPersonalProfile/otherPersonalAddresses/countryCode',
  '/applicantOnboarding/applicantWorkerProfile/homeWorkLocation/address/countryCode',
];

const governmentIdStatusCodeKey = '/applicantOnboarding/applicantPersonalProfile/governmentIDs/statusCode/code';
const governmentIdKey = '/applicantOnboarding/applicantPersonalProfile/governmentIDs/id';

const maritalStatusCodeTimeKey = '/applicantOnboarding/applicantPersonalProfile/maritalStatusCode/effectiveDateTime';
const maritalStatusCodeKey = '/applicantOnboarding/applicantPersonalProfile/maritalStatusCode/code';

const ethnicityCodeKey = '/applicantOnboarding/applicantPersonalProfile/ethnicityCode/code';
const raceCodeKey = '/applicantOnboarding/applicantPersonalProfile/raceCode/code';

const fileNumberKey = '/applicantOnboarding/applicantPayrollProfile/payrollFileNumber';
const associateIdKey = '/applicantOnboarding/workerID/id';
const federalFilingStatusKey = '/applicantOnboarding/applicantTaxProfile/usFederalTaxInstruction/federalIncomeTaxInstruction/taxFilingStatusCode/code'
const federalExemptWithholdingKey  = '/applicantOnboarding/applicantTaxProfile/usFederalTaxInstruction/federalIncomeTaxInstruction/taxWithholdingStatus/statusCode/'
const multipleJobIndicatorKey =  '/applicantOnboarding/applicantTaxProfile/usFederalTaxInstruction/multipleJobIndicator'
const dependantsAmountKey = '/applicantOnboarding/applicantTaxProfile/usFederalTaxInstruction/federalIncomeTaxInstruction/taxAllowances/taxAllowanceAmount/dependantsAmount'
const deductionsAmountKey = '/applicantOnboarding/applicantTaxProfile/usFederalTaxInstruction/federalIncomeTaxInstruction/taxAllowances/taxAllowanceAmount/deductionsAmount'
const otherIncomeAmountKey = '/applicantOnboarding/applicantTaxProfile/usFederalTaxInstruction/federalIncomeTaxInstruction/additionalIncomeAmount/amount'
const extraTaxAmountKey = '/applicantOnboarding/applicantTaxProfile/usFederalTaxInstruction/federalIncomeTaxInstruction/additionalTaxAmount/amount'
const workedInstateKey = '/stateIncomeTaxInstructions/stateCode/code'
const stateMaritalStatusKey = '/stateIncomeTaxInstructions/taxFilingStatusCode/code'
const stateExemptionsKey = '/stateIncomeTaxInstructions/taxAllowanceQuantity';
const stateAdditionalTaxAmountKey = '/stateIncomeTaxInstructions/additionalTaxAmount/amount';
const workedInLocalityKey = '/applicantOnboarding/applicantTaxProfile/localTaxInstructions/localCode/workedInLocality';
const localExemptionsKey = '/applicantOnboarding/applicantTaxProfile/localTaxInstructions/taxAllowanceQuantity';
const taxFreeStateList = ['AK', 'AZ', 'AR', 'CT', 'FL', 'IL', 'IN', 'KY', 'MI', 'MT', 'NV', 'NH', 'OH', 'RI', 'SD', 'TN', 'TX', 'VA','WA', 'WY', 'XX', 'GU', 'VI']

const hispanicOrLatinoCode = '3';

// Tech Debt: See details: https://workstreamhq.atlassian.net/browse/WS-28008
const manualMarkedPrefilledFields = [dependantsAmountKey, deductionsAmountKey, otherIncomeAmountKey, extraTaxAmountKey, federalFilingStatusKey, workedInstateKey]

const getComponentType = (adpQuestion) => {
  if (adpQuestion.question_type === 'adp_phone_number') return TYPES.single_question;
  const { fields, max_items } = adpQuestion;
  if (fields === undefined) return TYPES.single_question;
  if (fields && max_items === undefined) return TYPES.group_question;
  if (fields && max_items) return TYPES.extendable_group_question;
  return undefined;
};

const getNestedFields = (question) => {
  const { fields } = question;
  const nestFields = fields.filter((field) => field.fields);
  return nestFields;
};

const getAdpQuestionInputName = (adpQuestion, fieldKey, rowIndex, nestedFieldKey) => {
  const componentType = getComponentType(adpQuestion);
  let name = '';
  const isNestedQuestionGroup = nestedFieldKey && rowIndex !== undefined && getNestedFields(adpQuestion).length !== 0;
  const { onboarding_form_question_id: questionId } = adpQuestion;
  switch (componentType) {
    case TYPES.single_question:
      name = `${questionId}[${adpQuestion.key}]`;
      break;
    case TYPES.group_question:
      if (isNestedQuestionGroup) {
        name = `${questionId}[${fieldKey}][${rowIndex}][${nestedFieldKey}]`;
      } else {
        name = `${questionId}[${fieldKey}]`;
      }
      break;
    case TYPES.extendable_group_question:
      name = `${questionId}[${rowIndex}][${fieldKey}]`;
      break;
    default:
  }
  return name;
};

const TYPES = {
  single_question: 'single question',
  group_question: 'group questions',
  extendable_group_question: 'extendable_group_question',
};

const hasTaxFieldValue = (value) => value !== undefined && value !== null && value !== '';

// ADP rejects a state/local tax row that has values but no worked-in state/locality code,
// so the row anchor field becomes required as soon as any dependent field is filled
const taxRowDependencyRules = {
  adp_state_tax: {
    requiredFieldKey: workedInstateKey,
    dependentFieldKeys: [stateMaritalStatusKey, stateExemptionsKey, stateAdditionalTaxAmountKey],
    errorMessage: 'Worked In State is required when other state tax fields are filled',
  },
  adp_local_tax: {
    requiredFieldKey: workedInLocalityKey,
    dependentFieldKeys: [localExemptionsKey],
    errorMessage: 'Worked In Locality is required when Local Exemptions is filled',
  },
};

const validateTaxRowDependencies = (adpQuestion) => {
  const rowErrors = {};
  const rule = taxRowDependencyRules[adpQuestion.question_type];
  if (!rule || !adpQuestion.answer || !adpQuestion.fields) return rowErrors;
  const nestedFields = getNestedFields(adpQuestion);
  const nestedFieldKey = nestedFields.length !== 0 ? nestedFields[0].key : undefined;
  const { answer } = adpQuestion.answer;
  const rowsSource = nestedFieldKey ? answer[nestedFieldKey] : answer;
  const rows = _.isArray(rowsSource) ? rowsSource : [rowsSource];
  rows.forEach((row, rowIdx) => {
    if (!row) return;
    const hasDependentValue = rule.dependentFieldKeys.some(
      (fieldKey) => hasTaxFieldValue(row[fieldKey]),
    );
    if (!hasDependentValue || hasTaxFieldValue(row[rule.requiredFieldKey])) return;
    let inputName;
    if (nestedFieldKey) {
      inputName = getAdpQuestionInputName(
        adpQuestion,
        nestedFieldKey,
        rowIdx,
        rule.requiredFieldKey,
      );
    } else if (_.isArray(rowsSource)) {
      inputName = getAdpQuestionInputName(adpQuestion, rule.requiredFieldKey, rowIdx);
    } else {
      inputName = getAdpQuestionInputName(adpQuestion, rule.requiredFieldKey);
    }
    rowErrors[inputName] = rule.errorMessage;
  });
  return rowErrors;
};

class ADPFormActionContent extends Component {
  constructor(props) {
    super(props);

    const { prefilledData = {}, action } = props;

    // TODO- Use this config to get the company code
    const config = this.props.config

    this.handleSubmitForm = this.handleSubmitForm.bind(this);

    const prefilledDataKeys = [];
    prefilledDataKeys.push(...manualMarkedPrefilledFields);

    const formSubmission = (() => {
      const { assignees, action_type: actionType, status: actionStatus } = action;
      if (actionType === 'hr_fill_adp_form' && actionStatus === 'not_started') {
        // get form_submission from current login company staff assignee
        const { companyStaff } = props;
        const companyStaffAssignee = _.find(assignees, { company_staff: { id: companyStaff.id } });
        if (!companyStaffAssignee || !companyStaffAssignee.form_submission) return {};
        return companyStaffAssignee.form_submission;
      } if (actionType === 'hr_fill_adp_form' && actionStatus !== 'not_started') {
        // get form_submission from master assignee
        const masterAssignee = _.find(assignees, { is_master: true });
        if (!masterAssignee || !masterAssignee.form_submission) return {};
        return masterAssignee.form_submission;
      }
      // For the 'employee_fill_adp_form' action, get form_submission from the first assignee
      return (assignees[0] && assignees[0].form_submission) || {};
    })();
    const { digest_key: formDigestKey, questions: questionsFromSubmission = [] } = formSubmission;

    const questions = questionsFromSubmission.map((question) => {
      const adpPhoneNumberKey = 'adp_phone_number';
      // transform the 'adp_phone_number' type question
      if (question.question_type === adpPhoneNumberKey) {
        let answer;
        if (question.answer && question.answer.answer[0][question.fields[0].key]) {
          const answerItem = question.answer.answer[0];
          const { fields } = question;
          const countryDialing = answerItem[fields[0].key];
          const areaDialing = answerItem[fields[1].key] || '';
          const dialNumber = answerItem[fields[2].key];
          answer = {
            ...question.answer.answer,
            answer: { adp_phone_number: `+${countryDialing}${areaDialing}${dialNumber}` },
          };
        } else if (question.group === 'mobiles' && prefilledData['/applicant/person/communication/mobiles/dialNumber']) {
          // above object is referenced in ADP v1 APIs
          const adpNumber = `+${prefilledData['/applicant/person/communication/mobiles/countryDialing']}${prefilledData['/applicant/person/communication/mobiles/dialNumber']}`;
          answer = {
            answer: { adp_phone_number: adpNumber },
          };
          prefilledDataKeys.push(adpPhoneNumberKey);
        } else if (question.group === 'applicantPersonalProfile/communication/mobiles' && prefilledData['/applicantOnboarding/applicantPersonalProfile/communication/mobiles/dialNumber']) {
          // above object is referenced in ADP v2 APIs
          const adpNumber = `+${prefilledData['/applicantOnboarding/applicantPersonalProfile/communication/mobiles/countryDialing']}${prefilledData['/applicantOnboarding/applicantPersonalProfile/communication/mobiles/dialNumber']}`;
          answer = {
            answer: { adp_phone_number: adpNumber },
          };
          prefilledDataKeys.push(adpPhoneNumberKey);
        }
        return {
          ...question,
          key: adpPhoneNumberKey,
          answer,
        };
      }
      return question;
    });

    const questionsWithDefaultAnswers = questions
      && questions.map((adpQuestion) => {
        // if (adpQuestion.answer) return adpQuestion; // Do not touch if answer key exists
        let questionWithAnswer = { ...adpQuestion };
        switch (getComponentType(adpQuestion)) {
          case TYPES.single_question:
            if (adpQuestion.answer && (adpQuestion.answer.answer[adpQuestion.key] || adpQuestion.answer.answer[adpQuestion.key] === false)) break;
            const answer = prefilledData[adpQuestion.key] || '';
            if (answer) {
              prefilledDataKeys.push(adpQuestion.key);
            }
            questionWithAnswer = set(`answer.answer.${adpQuestion.key}`, answer, questionWithAnswer);

            // HRs can change these 2 fields from required to non-required
            if (adpQuestion.key === fileNumberKey || adpQuestion.key === associateIdKey) {
              if (adpQuestion.answer && adpQuestion.answer.answer[adpQuestion.key] == '') {
                questionWithAnswer.temporaryDisableRequired = true;
              }
            }
            break;
          case TYPES.group_question:
            for (let i = 0; i < adpQuestion.fields.length; i += 1) {
              const field = adpQuestion.fields[i];
              // Hack Marital Status and Marital Effective Date
              if (field.key === maritalStatusCodeTimeKey) {
                const relatedFieldKey = maritalStatusCodeKey;
                const relatedFieldAnswer = adpQuestion.answer && adpQuestion.answer.answer[relatedFieldKey];
                if (relatedFieldAnswer === 'S') {
                  field.temporaryDisableRequired = true;
                  field.disabled = true;
                }
              }

              // Hack Ethnicity and Race
              if (field.key === raceCodeKey) {
                const ethnicityAnswer = adpQuestion.answer && adpQuestion.answer.answer[ethnicityCodeKey];
                if (ethnicityAnswer === hispanicOrLatinoCode) {
                  field.temporaryDisableRequired = true;
                  field.disabled = true;
                }
              }
              
              if (adpQuestion.answer && (adpQuestion.answer.answer[field.key] || adpQuestion.answer.answer[field.key] === false)) continue;

              let fieldAnswer = prefilledData[field.key] || '';
              if (fieldAnswer) {
                prefilledDataKeys.push(field.key);
              }

              // Set default value of federalExemptWithholdingKey to false
              if (field.key === federalExemptWithholdingKey) {
                fieldAnswer = false;
                prefilledDataKeys.push(field.key);
              }

              if (field.fields) {
                field.fields.forEach((f) => {
                  questionWithAnswer = set(`answer.answer.${field.key}[0].${f.key}`, fieldAnswer, questionWithAnswer);
                });
              } else {
                questionWithAnswer = set(`answer.answer.${field.key}`, fieldAnswer, questionWithAnswer);
              }
            }
            break;
          case TYPES.extendable_group_question:
            for (let i = 0; i < adpQuestion.fields.length; i += 1) {
              const field = adpQuestion.fields[i];
              if (field.key === governmentIdStatusCodeKey) {
                const governmentId = adpQuestion.answer && adpQuestion.answer.answer[0][governmentIdKey];
                if (governmentId) {
                  field.temporaryDisableRequired = true;
                  field.disabled = true;
                }
                questionWithAnswer = set(`fields[${i}]`, field, questionWithAnswer);
              } else if (field.key === governmentIdKey) {
                const relatedFieldKey = governmentIdStatusCodeKey;
                const relatedFieldAnswer = adpQuestion.answer && adpQuestion.answer.answer[0][relatedFieldKey];
                if (relatedFieldAnswer) {
                  field.temporaryDisableRequired = true;
                }
                questionWithAnswer = set(`fields[${i}]`, field, questionWithAnswer);
              }

              if (adpQuestion.answer && (adpQuestion.answer.answer[0][field.key] || adpQuestion.answer.answer[0][field.key] === false)) continue;
              const answer = prefilledData[field.key] || '';
              if (answer) {
                prefilledDataKeys.push(field.key);
              }
              // Remove dashes from SSN answer, if any
              const formattedAnswer = field.key === governmentIdKey ? answer.replace(/-/g, '') : answer;
              questionWithAnswer = set(`answer.answer[0].${field.key}`, formattedAnswer, questionWithAnswer);
            }
            break;
          default:
            break;
        }
        return questionWithAnswer;
      });
    this.state = {
      questions: questionsWithDefaultAnswers,
      errorMessageMap: {},

      // following states are for review process
      changedQuestions: {},
      // Do not change the value of `readOnlyQuestions`, this is only for query the original answer when reviewer uncheck
      // the question edit button
      readOnlyQuestions: { ...questionsWithDefaultAnswers },
      prefilledDataKeys,
      formDigestKey,
    };
  }

  handleExtendQuestionGroup(questionId) {
    const foundQuestion = _.find(this.state.questions, {
      onboarding_form_question_id: questionId,
    });
    if (!foundQuestion) return;
    let { answer } = foundQuestion;
    // By default there is no answers key in each question, only
    // submitted question gonna have answers key
    if (answer === undefined) {
      answer = { answer: [{}, {}] };
    } else {
      answer.answer.push({});
    }
    const questions = this.state.questions.map((q) => {
      if (q.onboarding_form_question_id !== questionId) return q;
      return {
        ...q,
        answer,
      };
    });
    this.setState({ questions });
  }

  handleSubmitForm(e) {
    e.preventDefault();
    const { action } = this.props;
    // validate form
    const { questions } = this.state;
    const errorMessageMap = {};
    for (let i = 0; i < questions.length; i += 1) {
      const adpQuestion = questions[i];
      const componentType = getComponentType(adpQuestion);
      if (adpQuestion.question_type === 'adp_home_org_units') {
        for (let i = 0; i < adpQuestion.answer.answer.length; i += 1) {
          const answerItem = adpQuestion.answer.answer[i];
          const typeCodeValue = answerItem[adpQuestion.fields[0].key];
          const nameCodeValue = answerItem[adpQuestion.fields[1].key];
          if (!typeCodeValue && !nameCodeValue && !adpQuestion.is_required) continue;
          if (!typeCodeValue || !nameCodeValue) {
            errorMessageMap[
              getAdpQuestionInputName(adpQuestion, 'adp_home_org_units', i)
            ] = 'Please complete the selection.';
          }
        }
        continue;
      }

      if (adpQuestion.question_type === 'adp_phone_number') {
        // special check logic for apd_phone_number type question
        const value = adpQuestion.answer.answer.adp_phone_number;
        if (!value && !adpQuestion.is_required) continue;
        if (!parsePhoneNumberFromString(value) || !parsePhoneNumberFromString(value).isValid()) {
          errorMessageMap[
            getAdpQuestionInputName(adpQuestion)
          ] = 'Please enter correct phone number.';
        }
        continue;
      }

      switch (componentType) {
        case TYPES.single_question:
          const { rules } = adpQuestion;
          let value = adpQuestion.answer.answer[adpQuestion.key];
          if ((!adpQuestion.is_required || adpQuestion.temporaryDisableRequired) && (value === '' || value.length === 0)) continue;
          if (adpQuestion.question_type === 'date' && value) {
            // date should be MM/DD/YYYY and can be converted to YYYY-MM-DD for validations
            if (!moment(value, 'MM/DD/YYYY', true).isValid()) {
              errorMessageMap[getAdpQuestionInputName(adpQuestion)] = 'Please use correct format MM/DD/YYYY';
              continue;
            } else {
              value = moment(value).format('YYYY-MM-DD');
            }
          }
          const error = getErrorMessage(value, rules);
          if (error) {
            errorMessageMap[getAdpQuestionInputName(adpQuestion)] = error;
          }
          break;
        case TYPES.group_question:
          for (let i = 0; i < adpQuestion.fields.length; i += 1) {
            const fieldItem = adpQuestion.fields[i];
            const nestedFields = getNestedFields(adpQuestion);
            if (fieldItem && nestedFields.length !== 0) {
              const { fields } = nestedFields[0];
              for (let n = 0; n < fields.length; n += 1) {
                const nestedFieldItem = fields[n];
                for (let rowIdx = 0; rowIdx < adpQuestion.answer.answer[nestedFields[0].key].length; rowIdx += 1) {
                  const { rules } = nestedFieldItem;
                  let value = adpQuestion.answer.answer[nestedFields[0].key][rowIdx][nestedFieldItem.key];
                  if ((!nestedFieldItem.is_required || nestedFieldItem.temporaryDisableRequired) && (value === '' || value.length === 0)) continue;
                  if (nestedFieldItem.question_type === 'date' && value) {
                    // date should be MM/DD/YYYY and can be converted to YYYY-MM-DD for validations
                    if (!moment(value, 'MM/DD/YYYY', true).isValid()) {
                      errorMessageMap[getAdpQuestionInputName(adpQuestion, fieldItem.key, rowIdx, nestedFieldItem.key)] = 'Please use correct format MM/DD/YYYY';
                      continue;
                    } else {
                      value = moment(value).format('YYYY-MM-DD');
                    }
                  }
                  const error = getErrorMessage(value, rules);
                  if (error) {
                    errorMessageMap[
                      getAdpQuestionInputName(adpQuestion, fieldItem.key, rowIdx, nestedFieldItem.key)
                    ] = error;
                  }
                }
              }
            }
            const { rules } = fieldItem;
            let value = adpQuestion.answer.answer[fieldItem.key];
            if ((!fieldItem.is_required || fieldItem.temporaryDisableRequired) && (value === '' || value.length === 0)) continue;
            if (fieldItem.question_type === 'date' && value) {
              // date should be MM/DD/YYYY and can be converted to YYYY-MM-DD for validations
              if (!moment(value, 'MM/DD/YYYY', true).isValid()) {
                errorMessageMap[getAdpQuestionInputName(adpQuestion, fieldItem.key)] = 'Please use correct format MM/DD/YYYY';
                continue;
              } else {
                value = moment(value).format('YYYY-MM-DD');
              }
            }
            const error = getErrorMessage(value, rules);
            if (error) {
              errorMessageMap[
                getAdpQuestionInputName(adpQuestion, fieldItem.key)
              ] = error;
            }
          }
          break;
        case TYPES.extendable_group_question:
          for (let rowIdx = 0; rowIdx < adpQuestion.answer.answer.length; rowIdx += 1) {
            const answerItem = adpQuestion.answer.answer[rowIdx];
            for (let j = 0; j < adpQuestion.fields.length; j += 1) {
              const fieldItem = adpQuestion.fields[j];
              let value = answerItem[fieldItem.key];
              const { rules } = fieldItem;
              if ((!fieldItem.is_required || fieldItem.temporaryDisableRequired) && (value === '' || value.length === 0)) continue;
              if (fieldItem.question_type === 'date' && value) {
                // date should be MM/DD/YYYY and can be converted to YYYY-MM-DD for validations
                if (!moment(value, 'MM/DD/YYYY', true).isValid()) {
                  errorMessageMap[getAdpQuestionInputName(adpQuestion, fieldItem.key, rowIdx)] = 'Please use correct format MM/DD/YYYY';
                  continue;
                } else {
                  value = moment(value).format('YYYY-MM-DD');
                }
              }
              const error = getErrorMessage(value, rules);
              if (error) {
                errorMessageMap[
                  getAdpQuestionInputName(adpQuestion, fieldItem.key, rowIdx)
                ] = error;
              }
            }
          }
          break;
        default:
      }

      Object.assign(errorMessageMap, validateTaxRowDependencies(adpQuestion));
    }
    if (Object.keys(errorMessageMap).length > 0) {
      this.setState({ errorMessageMap });
      return;
    }

    if (this.props.previewMode) {
      alert("Congrats, but form won't be submitted in preview mode.");
      return;
    }

    const formPayload = this.state.questions.reduce(
      (acc, adpQuestion) => {
        const { onboarding_form_question_id: questionId } = adpQuestion;
        if (adpQuestion.question_type === 'adp_phone_number') {
          const value = adpQuestion.answer.answer.adp_phone_number;
          const { fields } = adpQuestion;
          const countryCallingCodeKey = _.find(fields, { question_type: 'adp_country_code' }).key;
          const areaDialingKey = _.find(fields, { question_type: 'adp_area_code' }).key;
          const dialNumberKey = _.find(fields, { question_type: 'adp_dial_number' }).key;

          let countryCode = '';
          let areaDialing = '';
          let dialNumber = '';
          if (value) {
            const phoneNumberObject = parsePhoneNumberFromString(
              adpQuestion.answer.answer.adp_phone_number,
            );
            const { countryCallingCode, nationalNumber } = phoneNumberObject;
            countryCode = countryCallingCode;
            areaDialing = nationalNumber.slice(0, 3);
            dialNumber = nationalNumber.slice(3);
          }
          acc[questionId] = [
            {
              [countryCallingCodeKey]: countryCode,
              [areaDialingKey]: areaDialing,
              [dialNumberKey]: dialNumber,
            },
          ];
        } else {
          const componentType = getComponentType(adpQuestion);
          switch (componentType) {
            case TYPES.single_question:
              if (adpQuestion.question_type === 'date') {
                // here date should be MM/DD/YYYY and can be converted to YYYY-MM-DD
                let value = adpQuestion.answer.answer[adpQuestion.key];
                if (value) {
                  value = moment(value, 'MM/DD/YYYY', true).isValid() ? moment(value).format('YYYY-MM-DD') : '';
                  adpQuestion = set(`answer.answer.${adpQuestion.key}`, value, adpQuestion);
                }
              }
              break;
            case TYPES.group_question:
              for (let i = 0; i < adpQuestion.fields.length; i += 1) {
                const fieldItem = adpQuestion.fields[i];

                if (fieldItem.question_type === 'date') {
                  // here date should be MM/DD/YYYY and can be converted to YYYY-MM-DD
                  let value = adpQuestion.answer.answer[fieldItem.key];
                  if (value) {
                    value = moment(value, 'MM/DD/YYYY', true).isValid() ? moment(value).format('YYYY-MM-DD') : '';
                    adpQuestion = set(`answer.answer.${fieldItem.key}`, value, adpQuestion);
                  }
                }
              }
              break;
            case TYPES.extendable_group_question:
              for (let rowIdx = 0; rowIdx < adpQuestion.answer.answer.length; rowIdx += 1) {
                const answerItem = adpQuestion.answer.answer[rowIdx];
                for (let j = 0; j < adpQuestion.fields.length; j += 1) {
                  const fieldItem = adpQuestion.fields[j];
                  if (fieldItem.question_type === 'date') {
                    // here date should be MM/DD/YYYY and can be converted to YYYY-MM-DD
                    let value = answerItem[fieldItem.key];
                    if (value) {
                      value = moment(value, 'MM/DD/YYYY', true).isValid() ? moment(value).format('YYYY-MM-DD') : '';
                      adpQuestion = set(`answer.answer[${rowIdx}].${fieldItem.key}`, value, adpQuestion);
                    }
                  }
                }
              }
              break;
            default:
          }
          acc[questionId] = adpQuestion.answer.answer;
        }
        return acc;
      },
      {},
    );
    if (this.props.reviewActions) {
      const hasChanged = _.find(
        Object.keys(this.state.changedQuestions),
        (key) => this.state.changedQuestions[key],
      );
      return {
        hasChanged,
        answers: formPayload,
        id: action.assignees[0].form_submission.id,
      };
    }

    const config = {
      headers: {
        ...(authCode && { Authorization: authCode }),
      },
      'Access-Control-Allow-Origin': '*',
    };

    const digestKey = this.state.formDigestKey;
    this.setState({ isSubmitting: true, saveDataError: undefined });
    Axios.put(
      APIURL.replace(':digest_key', digestKey),
      {
        onboarding_form_submission: {
          answers: formPayload,
        },
      },
      config,
    )
      .then((data) => {
        this.setState({ isSubmitting: false });
        if (this.props.onSubmitADPFormSuccess) {
          this.props.onSubmitADPFormSuccess(data);
        }
      })
      .catch((error) => {
        let saveDataError = 'Something went wrong while saving data.';
        if (error && error.response && error.response.data) {
          const { error_code: errorCode, message } = error.response.data;
          if (message) {
            saveDataError = message;
          }
          if (errorCode === 43001 || errorCode === 43002) {
            // Need to refresh the side bar action status
            if (this.props.refreshProgressDataFn) this.props.refreshProgressDataFn();
          }
        }
        this.setState({ isSubmitting: false, saveDataError });
      });
  }

  handleDeleteAnswerRow(questionId, rowIndex) {
    const foundQuestion = _.find(this.state.questions, {
      onboarding_form_question_id: questionId,
    });
    if (!foundQuestion) return;
    const answer = foundQuestion.answer.answer.filter((a, idx) => idx !== rowIndex);
    const questions = this.state.questions.map((q) => {
      if (q.onboarding_form_question_id !== questionId) return q;
      return {
        ...q,
        answer: {
          ...q.answer,
          answer,
        },
      };
    });

    const errorMessageMap = Object.keys(this.state.errorMessageMap).reduce((acc, errorKey) => {
      if (errorKey.indexOf(`${questionId}[${rowIndex}]`) === -1) {
        acc[errorKey] = this.state.errorMessageMap[errorKey];
      }
      return acc;
    }, {});

    this.setState({ questions, errorMessageMap });
  }

  handleTemporaryDisableRequired({ name, checked, adpQuestion }) {
    let updatedQuestion = { ...adpQuestion };
    updatedQuestion = set(`answer.answer.${adpQuestion.key}`, '', updatedQuestion);
    updatedQuestion.temporaryDisableRequired = checked;

    const questions = this.state.questions.map((q) => {
      if (q.onboarding_form_question_id !== updatedQuestion.onboarding_form_question_id) return q;
      return updatedQuestion;
    });

    const errorMessageMap = {
      ...this.state.errorMessageMap,
      [name]: undefined,
    };
    this.setState({ questions, errorMessageMap });
  }

  handleQuestionChange({
    name, value, adpQuestion, fieldKey, rowIndex, fieldQuestion,
  }) {
    let updatedQuestion = { ...adpQuestion };
    switch (getComponentType(adpQuestion)) {
      case TYPES.single_question:
        updatedQuestion = set(`answer.answer.${adpQuestion.key}`, value, updatedQuestion);
        break;
      case TYPES.group_question:
        if (fieldQuestion && getNestedFields(adpQuestion).length !== 0) {
          const combindValue = {
            ...updatedQuestion.answer.answer[`${fieldQuestion.key}`][`${rowIndex}`],
          };
          combindValue[fieldKey] = value;
          updatedQuestion = set(`answer.answer.${fieldQuestion.key}[${rowIndex}]`, combindValue, updatedQuestion);
        } else {
          updatedQuestion = set(`answer.answer.${fieldKey}`, value, updatedQuestion);
        }

        break;
      case TYPES.extendable_group_question:
        updatedQuestion = set(`answer.answer[${rowIndex}].${fieldKey}`, value, updatedQuestion);
      default:
    }

    if (fieldKey === governmentIdStatusCodeKey) {
      const formattedFields = updatedQuestion.fields.map((f) => {
        if (f.key === governmentIdKey) {
          return {
            ...f,
            disabled: value === true,
            temporaryDisableRequired: value === true,
          };
        }
        return f;
      });
      updatedQuestion = {
        ...updatedQuestion,
        fields: formattedFields,
      };
      updatedQuestion = set(`answer.answer[${rowIndex}].${governmentIdKey}`, '', updatedQuestion);
    }

    if (fieldKey === governmentIdKey) {
      const formattedFields = updatedQuestion.fields.map((f) => {
        if (f.key === governmentIdStatusCodeKey) {
          return {
            ...f,
            disabled: !!(value !== ''),
            temporaryDisableRequired: !!(value !== ''),
          };
        }

        if (f.key === fieldKey) {
          return {
            ...f,
            temporaryDisableRequired: false,
          };
        }
        return f;
      });
      updatedQuestion = {
        ...updatedQuestion,
        fields: formattedFields,
      };
      updatedQuestion = set(`answer.answer[${rowIndex}].${governmentIdStatusCodeKey}`, '', updatedQuestion);

      // Remove '-'s from input
      const governmentId = updatedQuestion.answer && updatedQuestion.answer.answer[`${rowIndex}`][governmentIdKey];
      updatedQuestion = set(`answer.answer[${rowIndex}].${governmentIdKey}`, governmentId.replace(/-/g, ''), updatedQuestion);
    }

    // Hack Marital Status and Marital Effective Date
    if (fieldKey === maritalStatusCodeKey) {
      const relatedFieldKey = maritalStatusCodeTimeKey;
      const formattedFields = updatedQuestion.fields.map((f) => {
        if (f.key === relatedFieldKey) {
          return {
            ...f,
            disabled: value === 'S',
            temporaryDisableRequired: value === 'S',
          };
        }
        return f;
      });
      updatedQuestion = {
        ...updatedQuestion,
        fields: formattedFields,
      };
      updatedQuestion = set(`answer.answer.${relatedFieldKey}`, '', updatedQuestion);
    }

    // Hack federal tax fields
    if (fieldKey === federalExemptWithholdingKey) {
      const formattedFields = updatedQuestion.fields.map((f) => {
        if (value === true) {
          if ((f.key === multipleJobIndicatorKey) || (f.key === dependantsAmountKey) || (f.key === deductionsAmountKey) || 
          (f.key === otherIncomeAmountKey)|| (f.key === extraTaxAmountKey)) {
            return {
              ...f,
              disabled: true,
              temporaryDisableRequired: true,
            };
          }
          updatedQuestion = set(`answer.answer.${multipleJobIndicatorKey}`, '', updatedQuestion);
          updatedQuestion = set(`answer.answer.${dependantsAmountKey}`, '', updatedQuestion);
          updatedQuestion = set(`answer.answer.${deductionsAmountKey}`, '', updatedQuestion);
          updatedQuestion = set(`answer.answer.${otherIncomeAmountKey}`, '', updatedQuestion);
          updatedQuestion = set(`answer.answer.${extraTaxAmountKey}`, '', updatedQuestion);
        } else {
          // value === false
          if ((f.key === multipleJobIndicatorKey) || (f.key === dependantsAmountKey) || (f.key === deductionsAmountKey) || 
            (f.key === otherIncomeAmountKey)|| (f.key === extraTaxAmountKey)) {
            return {
              ...f,
              disabled: false,
              temporaryDisableRequired: false,
            };
          }
        }
        return f;
      });
      updatedQuestion = {
        ...updatedQuestion,
        fields: formattedFields,
      };
    }
  
    // Hack state tax's state marital status field
    if (fieldKey === workedInstateKey) {
      const stateFields = updatedQuestion.fields[0]
      let nestedformattedFields = stateFields.fields.map((f) => {
        if (f.key === stateMaritalStatusKey) {
          return {
            ...f,
            disabled: taxFreeStateList.includes(value),
            temporaryDisableRequired: taxFreeStateList.includes(value),
          };
        }
        return f;
      });
      updatedQuestion = {
        ...updatedQuestion,
        fields: [
          { fields: nestedformattedFields, 
             key: fieldQuestion.key 
          }
        ]
      };
      updatedQuestion = set(`answer.answer.${fieldQuestion.key}[${rowIndex}].${stateMaritalStatusKey}`, '', updatedQuestion);
    }

    // Hack Ethnicity and Race
    if (fieldKey === ethnicityCodeKey) {
      const relatedFieldKey = raceCodeKey;
      const formattedFields = updatedQuestion.fields.map((f) => {
          if (f.key === relatedFieldKey) {
            return {
              ...f,
              disabled: value === hispanicOrLatinoCode,
              temporaryDisableRequired: value === hispanicOrLatinoCode
            };
          }
          return f;
      });
      updatedQuestion = {
          ...updatedQuestion,
          fields: formattedFields,
      };
      updatedQuestion = set(`answer.answer.${relatedFieldKey}`, '', updatedQuestion);
    }

    let questions = this.state.questions.map((q) => {
      if (q.onboarding_form_question_id !== updatedQuestion.onboarding_form_question_id) return q;
      return updatedQuestion;
    });

    // filter out the answers that /jobOffer/offerAssignment/homeOrganizationalUnits/typeCode/codeValue is Department, since it's related to the selected adp_company_code
    if (updatedQuestion.question_type === 'adp_company_code') {
      questions = questions.map((q) => {
        if (q.question_type !== 'adp_home_org_units') return q;
        const newAnswer = q.answer.answer.map((answer) => {
          const typeCode = answer[
            '/jobOffer/offerAssignment/homeOrganizationalUnits/typeCode/codeValue'
          ];
          if (typeCode !== 'Department') return answer;
          return {
            '/jobOffer/offerAssignment/homeOrganizationalUnits/typeCode/codeValue': 'Department',
            '/jobOffer/offerAssignment/homeOrganizationalUnits/nameCode/codeValue': '',
          };
        });
        return {
          ...q,
          answer: {
            ...q.answer,
            answer: newAnswer,
          },
        };
      });
    }

    const errorMessageMap = {
      ...this.state.errorMessageMap,
      [name]: undefined,
    };
    this.setState({ questions, errorMessageMap });
  }

  handleQuestionEditChange(questionId, inputName, checked) {
    const changedQuestions = {
      ...this.state.changedQuestions,
      [questionId]: checked,
    };
    const errorMessageMap = {
      ...this.state.errorMessageMap,
      [inputName]: undefined,
    };
    let questions;
    if (!checked) {
      // bring back the original answer
      const questionInProps = _.find(this.state.readOnlyQuestions, {
        onboarding_form_question_id: questionId,
      });
      if (questionInProps) {
        questions = this.state.questions.map(
          (question) => (question.onboarding_form_question_id === questionId ? questionInProps : question),
        );
      }
    }
    this.setState({ changedQuestions, errorMessageMap, ...(questions && { questions }) });
  }

  renderErrors() {
    const { action: { action_type: actionType, templateValid }, companyStaff } = this.props;
    let errorMessage = '';
    if (templateValid === false) {
      if (actionType === 'employee_fill_adp_form' && !companyStaff) {
        errorMessage = 'This form is outdated and cannot be submitted. Please contact your manager for assistance.';
      } else {
        errorMessage = 'This form is outdated and cannot be submitted. Please select a new onboarding template and restart the onboarding process for this employee.';
      }
    }
    return (
      errorMessage && (
        <Text color="red" style={{ width: '350px', marginBottom: '15px' }}>
          {errorMessage}
        </Text>
      )
    );
  }

  renderButtons() {
    // We have only one 'Submit' button in this component;
    if (this.props.reviewActions) return null;
    const { saveDataError } = this.state;

    const { previewMode } = this.props;
    if (previewMode) {
      return <DisabledActionButton />;
    }

    let disabled = false;
    let disableNote = '';
    const {
      action: {
        assignees, action_type: actionType, status: actionStatus, templateValid,
      }, companyStaff,
    } = this.props;

    if (actionStatus === 'completed') {
      return <DisabledActionButton disableNote="This step has been completed." />;
    }
    if (templateValid === false) {
      disabled = true;
      if (actionType === 'employee_fill_adp_form' && companyStaff && companyStaff.id) {
        disableNote = 'This form is outdated and cannot be submitted. Please select a new onboarding template and restart the onboarding process for this employee.';
      } else if (actionType === 'employee_fill_adp_form') {
        disableNote = 'This form is outdated and cannot be submitted. Please contact your manager for assistance.';
      } else if (actionType === 'hr_fill_adp_form') {
        disableNote = 'This form is outdated and cannot be submitted. Please select a new onboarding template and restart the onboarding process for this employee.';
      }
    } else if (actionStatus === 'not_started' && actionType === 'employee_fill_adp_form' && companyStaff && companyStaff.id) {
      disabled = true;
      disableNote = 'This step should be completed by the employee.';
    } else if (actionStatus === 'not_started' && actionType === 'hr_fill_adp_form' && !isActionAssigneeMatchCompanyStaffV2(assignees, companyStaff)) {
      const assigneeNames = (() => {
        if (!assignees || !assignees.length === 0 || !assignees[0]) return null;
        return assignees.map((item) => item.company_staff && item.company_staff.name);
      })();
      disabled = true;
      disableNote = `This step should be completed by ${assigneeNames.join(' or ')}.`;
    }

    return (
      <Flex column alignItems="flex-end">
        <Button
          disabled={disabled}
          onClick={this.handleSubmitForm}
          loading={this.state.isSubmitting}
        >
          Submit
        </Button>
        {disabled && <ActionNote text={disableNote} />}
        {saveDataError && <Text color="red">{saveDataError}</Text>}
      </Flex>
    );
  }

  getRequiredCategoryField(question) {
    const { questions: allQuestions } = this.state;
    let requiredQuestion = null;
    let requiredCategoryFieldTitle = null;
    if (question.category_type === '/applicantOnboarding/applicantWorkerProfile/job/occupationalClassifications/classificationCode') {
      question.category_type = '/applicantOnboarding/applicantWorkerProfile/job/occupationalClassifications/classificationID/code';
    }

    if (question.category_type) {
      allQuestions.forEach((q) => {
        if (q.key === question.category_type) {
          requiredCategoryFieldTitle = q.title;
          requiredQuestion = q;
        } else if (q.fields) {
          q.fields.forEach((f) => {
            if (f.key === question.category_type) {
              requiredCategoryFieldTitle = f.title;
              requiredQuestion = q;
            }
          });
          const nestedFields = getNestedFields(q);
          if (nestedFields.length !== 0) {
            nestedFields[0].fields.forEach((f) => {
              if (f.key === question.category_type) {
                requiredCategoryFieldTitle = f.title;
                requiredQuestion = q;
                // requiredField = f;
              }
            });
          }
        }
      });
    }
    return { requiredQuestion, requiredCategoryFieldTitle };
  }

  noDataError(type, option, question, field) {
    if (type === 'dropdown' && option.length === 0) {
      const title = field && question.fields && question.fields.length > 1
        ? question.title : field.title;
      return `There is no data setup for the question. Please check the ADP setup or turn '${title}' off temporarily.`;
    }
  }

  getRequiredCategoryFieldTitle(question) {
    const { requiredCategoryFieldTitle } = this.getRequiredCategoryField(question);
    return requiredCategoryFieldTitle;
  }

  getRequiredCategoryFieldAnswer(question, rowIdx) {
    const { requiredQuestion } = this.getRequiredCategoryField(question);

    let answer = requiredQuestion ? requiredQuestion.answer.answer : null;
    // Set category field answer for Worked In State and Lived in State fields
    if(question.category_type === '/applicantOnboarding/applicantPayrollProfile/payrollGroupCode' && this.props.config && this.props.config['company_code']) {
      return this.props.config['company_code']
    }

    if (requiredQuestion && requiredQuestion.fields && rowIdx !== undefined) {
      const nestedFields = getNestedFields(requiredQuestion);
      if (nestedFields.length > 0) {
        answer = requiredQuestion.answer.answer[nestedFields[0].key][rowIdx];
      } else if (requiredQuestion.max_items) {
        answer = requiredQuestion.answer.answer[rowIdx];
      }
    }
    return answer || answer === false ? answer[question.category_type] : '';
  }

  isRequiredCategoryValueExisted(question, rowIdx) {
    const answer = this.getRequiredCategoryFieldAnswer(question, rowIdx);
    return answer !== '';
  }

  requiredCategoryValue(question, rowIdx) {
    const answer = this.getRequiredCategoryFieldAnswer(question, rowIdx);
    return answer;
  }

  getOptions(field, rowIdx) {
    let options = field.options || (field.code_lists && field.code_lists[field.key]) || [];
    // Hack classificationCode here
    if (field.key === '/applicantOnboarding/applicantWorkerProfile/job/occupationalClassifications/classificationCode/code') {
      const occupationalClassificationsAnswer = this.getRequiredCategoryFieldAnswer(field, rowIdx);
      if (occupationalClassificationsAnswer === 'EEOC') {
        options = field.options;
      } else if (occupationalClassificationsAnswer === 'EEO') {
        options = field.code_lists[field.key];
      }
    }
    if (usStatesFieldKeys.includes(field.key)) {
      options = options.filter((op) => op.code === 'US');
    }
    return options;
  }

  render() {
    this.isOnboardingV2 = isOnboardingV2 === 'true';
    const { reviewActions, viewResultAsManager, action: { status: actionStatus } } = this.props;
    const {
      questions, errorMessageMap, changedQuestions, prefilledDataKeys,
    } = this.state;

    if (!questions) {
      if (this.isOnboardingV2) {
        return (
          <Flex column>
            <PageTitle title={this.props.action.module_name} />
            <Text color="gray" mt={20}>No question to display.</Text>
          </Flex>
        );
      }
      return (
        <Flex mb={20} flexDirection="column">
          <Text fontSize="22px" bold>{this.props.title}</Text>
          <Text color="gray" mt={20}>No question to display.</Text>
        </Flex>
      );
    }

    let sectionTitleFlag = initializeSectionTitleFlag();

    const items = questions.map((adpQuestion) => {
      const {
        fields,
        onboarding_form_question_id: questionId,
      } = adpQuestion;

      const componentType = getComponentType(adpQuestion);

      if (adpQuestion.question_type === 'adp_home_org_units') {
        const answers = adpQuestion.answer.answer;
        const subItems = answers.map((answer, idx) => (
          <ADP_HomeOrgUnit
            question={adpQuestion}
            key={`${questionId} - ${idx}`}
            title={adpQuestion.title}
            deletable={idx !== 0 && !reviewActions}
            onDeleteRow={() => {
              this.handleDeleteAnswerRow(questionId, idx);
            }}
            name={getAdpQuestionInputName(adpQuestion, 'adp_home_org_units', idx)}
            value={answer}
            errorMessage={
                errorMessageMap[getAdpQuestionInputName(adpQuestion, 'adp_home_org_units', idx)]
              }
            onChange={({ name, value, fieldKey }) => {
              this.handleQuestionChange({
                name,
                value,
                adpQuestion,
                fieldKey,
                rowIndex: idx,
              });
            }}
            disabled={viewResultAsManager || (reviewActions && changedQuestions[questionId])}
            reviewActions={reviewActions}
            isEditChecked={changedQuestions[questionId]}
            onCheckEdit={(checked) => {
              const inputName = getAdpQuestionInputName(adpQuestion, 'adp_home_org_units', idx);
              this.handleQuestionEditChange(questionId, inputName, checked);
            }}
              // this question rely on adp_company_code when department codeType is selected
            companyCode={(() => {
              const foundQuestion = _.find(questions, { question_type: 'adp_company_code' });
              if (!foundQuestion) return undefined;
              const value = foundQuestion.answer.answer[foundQuestion.fields[0].key];
              return value;
            })()}
          />
        ));
        return (
          <QuestionGroup
            key={questionId}
            extendable={adpQuestion.answer.answer.length < adpQuestion.max_items && !reviewActions}
            onExtend={() => {
              this.handleExtendQuestionGroup(questionId);
            }}
          >
            {subItems}
          </QuestionGroup>
        );
      }

      const originalQuestion = _.find(this.state.readOnlyQuestions, {
        onboarding_form_question_id: questionId,
      });

      const nestedGroupQuestion = (fieldQuestion, fields) => {
        let { answer } = fieldQuestion;
        if (!answer) answer = { answer: [{}] };
        answer = answer.answer[fieldQuestion.key];
        const subItems = answer.map((answer, rowIdx) => {
          const questionsRow = fields.map((field, idx) => {
            const questionDisabled = field.disabled || viewResultAsManager
              || (reviewActions && changedQuestions[questionId])
              || (!!originalQuestion.answer.answer[fieldQuestion.key][0][field.key] && actionStatus === 'not_started' && !prefilledDataKeys.includes(field.key));
            const options = this.getOptions(field, rowIdx);

            let questionValue = fieldQuestion.answer.answer[fieldQuestion.key][rowIdx][field.key];

            if (fieldQuestion.question_type === 'date') {
              // YYYY-MM-DD is loaded from backend, but show MM/DD/YYYY
              if (moment(questionValue, 'YYYY-MM-DD', true).isValid() || moment(questionValue, 'YYYY-MM-DDTHH:mm:ss.SSSSZ', true).isValid() || moment(questionValue).isValid()) {
                questionValue = moment(questionValue).format('MM/DD/YYYY');
                fieldQuestion.answer.answer[fieldQuestion.key][rowIdx][field.key] = questionValue;
              }
            }

            const optionLabelDescriptionFirst = !questionOptionsUseNameKeys.includes(field.key);
            sectionTitleFlag = setSectionTitleFlag(field.key, sectionTitleFlag);
            return (
              <>
              {renderSectionTitleBasedOnFlag(sectionTitleFlag)}
              <Question
                key={`${questionId}-${rowIdx}-${field.key}`}
                title={field.title}
                name={getAdpQuestionInputName(adpQuestion, fieldQuestion.key, rowIdx, field.key)}
                type={field.question_type}
                isRequired={!!field.is_required && !field.temporaryDisableRequired}
                value={questionValue || questionValue === false ? questionValue : ''}
                errorMessage={
                  errorMessageMap[getAdpQuestionInputName(adpQuestion, fieldQuestion.key, rowIdx, field.key)]
                }
                options={options}
                optionLabelDescriptionFirst={optionLabelDescriptionFirst}
                onChange={({ name, value }) => {
                  this.handleQuestionChange({
                    name,
                    value,
                    adpQuestion,
                    fieldKey: field.key,
                    rowIndex: rowIdx,
                    fieldQuestion,
                  });
                }}
                dateFormat={field.date_format && field.date_format.toUpperCase()}
                deletable={rowIdx !== 0 && !reviewActions}
                onDeleteRow={() => {
                  this.handleDeleteAnswerRow(questionId, rowIdx);
                }}
                disabled={questionDisabled}
                isEditChecked={changedQuestions[questionId]}
                reviewActions={reviewActions}
                onCheckEdit={(checked) => {
                  const inputName = getAdpQuestionInputName(adpQuestion, fieldQuestion.key, rowIdx, field.key);
                  this.handleQuestionEditChange(questionId, inputName, checked);
                }}
                categoryType={field.category_type}
                isRequiredCategoryValueExisted={this.isRequiredCategoryValueExisted(field, rowIdx)}
                requiredCategoryValue={this.requiredCategoryValue(field, rowIdx)}
                requiredCategoryTitle={this.getRequiredCategoryFieldTitle(field)}
                noDataError={this.noDataError(field.question_type, options, adpQuestion, field)}
              />
              </>
            );
          });
          return (
            <InputRowWrapper key={`${questionId}-${rowIdx}`}>
              {questionsRow}
            </InputRowWrapper>
          );
        });

        return (
          <QuestionGroup
            key={questionId}
            extendable={
              componentType === TYPES.extendable_group_question
              && fieldQuestion.answer.answer[fieldQuestion.key].length < fieldQuestion.max_items
            }
            onExtend={() => {
              this.handleExtendQuestionGroup(questionId);
            }}
          >
            {subItems}
          </QuestionGroup>
        );
      };

      switch (componentType) {
        case TYPES.single_question:
          const questionDisabled = adpQuestion.disabled || viewResultAsManager
            || (reviewActions && changedQuestions[questionId])
            || (!!originalQuestion.answer.answer[adpQuestion.key] && actionStatus === 'not_started' && !prefilledDataKeys.includes(adpQuestion.key));
          const options = this.getOptions(adpQuestion);
          let questionValue = adpQuestion.answer.answer[adpQuestion.key];

          if (adpQuestion.question_type === 'date') {
            // YYYY-MM-DD is loaded from backend, but show MM/DD/YYYY
            if (moment(questionValue, 'YYYY-MM-DD', true).isValid() || moment(questionValue, 'YYYY-MM-DDTHH:mm:ss.SSSSZ', true).isValid() || moment(questionValue).isValid()) {
              questionValue = moment(questionValue).format('MM/DD/YYYY');
              adpQuestion.answer.answer[adpQuestion.key] = questionValue;
            }
          }

          const optionLabelDescriptionFirst = !questionOptionsUseNameKeys.includes(adpQuestion.key);
          sectionTitleFlag = setSectionTitleFlag(adpQuestion.key, sectionTitleFlag);
          return (
            <>
            {renderSectionTitleBasedOnFlag(sectionTitleFlag)}
            <Question
              key={questionId}
              title={adpQuestion.title}
              name={getAdpQuestionInputName(adpQuestion)}
              errorMessage={errorMessageMap[getAdpQuestionInputName(adpQuestion)]}
              value={questionValue || questionValue === false ? questionValue : ''}
              type={adpQuestion.question_type}
              options={options}
              optionLabelDescriptionFirst={optionLabelDescriptionFirst}
              isRequired={!!adpQuestion.is_required && !adpQuestion.temporaryDisableRequired}
              dateFormat={adpQuestion.date_format && adpQuestion.date_format.toUpperCase()}
              onChange={({ name, value }) => {
                this.handleQuestionChange({
                  name,
                  value,
                  adpQuestion,
                });
              }}
              temporaryDisableRequired={adpQuestion.temporaryDisableRequired}
              onSetTemporaryDisableRequired={({ checked }) => {
                this.handleTemporaryDisableRequired({
                  name,
                  checked,
                  adpQuestion,
                });
              }}
              disabled={questionDisabled}
              reviewActions={reviewActions}
              isEditChecked={changedQuestions[questionId]}
              onCheckEdit={(checked) => {
                const inputName = getAdpQuestionInputName(adpQuestion);
                this.handleQuestionEditChange(questionId, inputName, checked);
              }}
              categoryType={adpQuestion.category_type}
              isRequiredCategoryValueExisted={this.isRequiredCategoryValueExisted(adpQuestion)}
              requiredCategoryValue={this.requiredCategoryValue(adpQuestion)}
              requiredCategoryTitle={this.getRequiredCategoryFieldTitle(adpQuestion)}
              noDataError={this.noDataError(adpQuestion.question_type, options, adpQuestion)}
            />
            </>
          );
        case TYPES.group_question:
          let subItems = fields.map((field, idx) => {
            if (field.fields) {
              const nestedQuestions = {
                ...field,
                onboarding_form_question_id: adpQuestion.onboarding_form_question_id,
                answer: adpQuestion.answer,
                title: field.title ? field.title : adpQuestion.title,
              };
              return nestedGroupQuestion(nestedQuestions, field.fields);
            }
            const questionDisabled = field.disabled || viewResultAsManager
              || (reviewActions && changedQuestions[questionId])
              || (!!originalQuestion.answer.answer[field.key] && actionStatus === 'not_started' && !prefilledDataKeys.includes(field.key));

            const options = this.getOptions(field);
            let questionValue = adpQuestion.answer.answer[field.key];

            if (adpQuestion.question_type === 'date') {
              // YYYY-MM-DD is loaded from backend, but show MM/DD/YYYY
              if (moment(questionValue, 'YYYY-MM-DD', true).isValid() || moment(questionValue, 'YYYY-MM-DDTHH:mm:ss.SSSSZ', true).isValid() || moment(questionValue).isValid()) {
                questionValue = moment(questionValue).format('MM/DD/YYYY');
                adpQuestion.answer.answer[field.key] = questionValue;
              }
            }

            const optionLabelDescriptionFirst = !questionOptionsUseNameKeys.includes(field.key);
            sectionTitleFlag = setSectionTitleFlag(field.key, sectionTitleFlag);
            return (
              <>
              {renderSectionTitleBasedOnFlag(sectionTitleFlag)}
                <Question
                  key={`${questionId}-${field.key}`}
                  title={field.title}
                  name={getAdpQuestionInputName(adpQuestion, field.key)}
                  type={field.question_type}
                  value={questionValue || questionValue === false ? questionValue : ''}
                  errorMessage={errorMessageMap[getAdpQuestionInputName(adpQuestion, field.key)]}
                  options={options}
                  optionLabelDescriptionFirst={optionLabelDescriptionFirst}
                  isRequired={!!field.is_required && !field.temporaryDisableRequired}
                  dateFormat={field.date_format && field.date_format.toUpperCase()}
                  onChange={({ name, value }) => {
                    this.handleQuestionChange({
                      name,
                      value,
                      adpQuestion,
                      fieldKey: field.key,
                    });
                  }}
                  disabled={questionDisabled}
                  reviewActions={reviewActions}
                  isEditChecked={changedQuestions[questionId]}
                  onCheckEdit={(checked) => {
                    const inputName = getAdpQuestionInputName(adpQuestion, field.key);
                    this.handleQuestionEditChange(questionId, inputName, checked);
                  }}
                  categoryType={field.category_type}
                  isRequiredCategoryValueExisted={this.isRequiredCategoryValueExisted(field)}
                  requiredCategoryValue={this.requiredCategoryValue(field)}
                  requiredCategoryTitle={this.getRequiredCategoryFieldTitle(field)}
                  noDataError={this.noDataError(field.question_type, options, adpQuestion, field)}
                />
              </>
            );
          });
          return (
            <QuestionGroup key={questionId} extendable={false}>
              {subItems}
            </QuestionGroup>
          );
        case TYPES.extendable_group_question:
          let { answer } = adpQuestion;
          if (!answer) answer = { answer: [{}] };
          subItems = answer.answer.map((answer, rowIdx) => {
            const questionsRow = fields.map((field, idx) => {
              const questionDisabled = field.disabled || viewResultAsManager
                || (reviewActions && changedQuestions[questionId])
                || (!!originalQuestion.answer.answer[0][field.key] && actionStatus === 'not_started' && !prefilledDataKeys.includes(field.key));

              const options = this.getOptions(field, rowIdx);
              let questionValue = adpQuestion.answer.answer[rowIdx][field.key];

              // Set Social Security Number Status on initial render to false
              if (field.key === governmentIdStatusCodeKey && questionValue === '') {
                questionValue = false;
                adpQuestion.answer.answer[rowIdx][field.key] = false;
              }

              if (adpQuestion.question_type === 'date') {
                // YYYY-MM-DD is loaded from backend, but show MM/DD/YYYY
                if (moment(questionValue, 'YYYY-MM-DD', true).isValid() || moment(questionValue, 'YYYY-MM-DDTHH:mm:ss.SSSSZ', true).isValid() || moment(questionValue).isValid()) {
                  questionValue = moment(questionValue).format('MM/DD/YYYY');
                  adpQuestion.answer.answer[rowIdx][field.key] = questionValue;
                }
              }

              const optionLabelDescriptionFirst = !questionOptionsUseNameKeys.includes(field.key);
              sectionTitleFlag = setSectionTitleFlag(field.key, sectionTitleFlag);
              return (
                <>
                {renderSectionTitleBasedOnFlag(sectionTitleFlag)}
                <Question
                  key={`${questionId}-${rowIdx}-${field.key}`}
                  title={field.title}
                  name={getAdpQuestionInputName(adpQuestion, field.key, rowIdx)}
                  type={field.question_type}
                  isRequired={!!field.is_required && !field.temporaryDisableRequired}
                  value={questionValue || questionValue === false ? questionValue : ''}
                  errorMessage={
                    errorMessageMap[getAdpQuestionInputName(adpQuestion, field.key, rowIdx)]
                  }
                  options={options}
                  optionLabelDescriptionFirst={optionLabelDescriptionFirst}
                  onChange={({ name, value }) => {
                    this.handleQuestionChange({
                      name,
                      value,
                      adpQuestion,
                      fieldKey: field.key,
                      rowIndex: rowIdx,
                    });
                  }}
                  dateFormat={field.date_format && field.date_format.toUpperCase()}
                  deletable={rowIdx !== 0 && !reviewActions}
                  onDeleteRow={() => {
                    this.handleDeleteAnswerRow(questionId, rowIdx);
                  }}
                  disabled={questionDisabled}
                  isEditChecked={changedQuestions[questionId]}
                  reviewActions={reviewActions}
                  onCheckEdit={(checked) => {
                    const inputName = getAdpQuestionInputName(adpQuestion, field.key, rowIdx);
                    this.handleQuestionEditChange(questionId, inputName, checked);
                  }}
                  categoryType={field.category_type}
                  isRequiredCategoryValueExisted={this.isRequiredCategoryValueExisted(field, rowIdx)}
                  requiredCategoryValue={this.requiredCategoryValue(field, rowIdx)}
                  requiredCategoryTitle={this.getRequiredCategoryFieldTitle(field)}
                  noDataError={this.noDataError(field.question_type, options, adpQuestion, field)}
                />
                </>
              );
            });
            return (
              <InputRowWrapper key={`${questionId}-${rowIdx}`}>
                {questionsRow}
              </InputRowWrapper>
            );
          });

          return (
            <QuestionGroup
              key={questionId}
              extendable={
                componentType === TYPES.extendable_group_question
                && adpQuestion.answer.answer.length < adpQuestion.max_items
              }
              onExtend={() => {
                this.handleExtendQuestionGroup(questionId);
              }}
            >
              {subItems}
            </QuestionGroup>
          );
        default:
      }
    });

    return (
      <form style={this.isOnboardingV2 ? { paddingLeft: '4px', paddingRight: '4px' } : { width: this.props.reviewActions ? '310px' : '280px', margin: "auto" }}>
        { this.renderErrors()}
        {
          this.isOnboardingV2 && <PageTitle title={this.props.action.module_name} />
        }
        {
          !this.isOnboardingV2
            && <Flex mb={20}><Text fontSize="22px" bold>{this.props.title}</Text></Flex>
        }
        <Flex column ml={this.props.reviewActions && 24}>
          {items}
        </Flex>
        {this.renderButtons()}
      </form>
    );
  }
}

export default ADPFormActionContent;